import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import {InputText} from "primereact/inputtext";
import {Password} from "primereact/password";
import LoginDialogFields from "../fields/LoginDialogFields";
import {Button} from "primereact/button";
import Message from "../../log/Message";
import {MessageType} from "../../log/MessageInterface";
import AbstractDialog, {AbstractDialogProperties, AbstractDialogState} from "./AbstractDialog";
import DialogDefault from "../parameter/DialogDefault";
import DialogType from "../DialogType";
import Fields from "../fields/Fields";
import DialogFields from "../fields/DialogFields";
import {LoginType} from "../../models/LoginType";
import Translate from "../../translation/Translate";
import Icon from "../../components/Icon";
import PKCE from 'js-pkce';
import {
	AuthProvider,
	Information,
	OAuthProviderName,
	UserStorage
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import StorageItem from "../../StorageItem";
import FunctionHelper from "../../helper/FunctionHelper";
import Exception from "../../exception/Exception";
import OAuthError from "../../exception/OAuthError";
import OAuth from "../../../oauth/OAuth";
import portalConfiguration from "../../configuration/PortalConfiguration";
import Portal from "../../../portal/Portal";
import azure from "../../../../resources/images/oauth/azure.svg";
import auth0 from "../../../../resources/images/oauth/auth0.svg";
import MessageBox from "../../components/MessageBox";
import ErrorCode from "../../exception/ErrorCode";

export interface LoginDialogProperties extends AbstractDialogProperties {
	parameter?: LoginType;
}

export interface LoginDialogState extends AbstractDialogState {
	parameter?: LoginType;
	fields?: Fields;
	fieldErrors: Map<LoginDialogFields, string>;
}

export class LoginDialogDefinition extends AbstractDialog<LoginDialogProperties, LoginDialogState> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			parameter: this.getDefaultParameter(),
			fieldErrors: new Map<LoginDialogFields, string>(),
			fields: DialogFields.fields.get(this.getDialogType())
		};
	}

	protected renderIcon(provider: AuthProvider): ReactNode {
		switch (provider.name) {
			case OAuthProviderName.AzureAd:
				return <img src={azure} alt={provider.name}/>;
			case OAuthProviderName.Auth0:
				return <img src={auth0} alt={provider.name}/>;
		}

		return <Icon icon={"p-button-icon p-c p-button-icon-left icon-user"}/>;
	}

	protected renderButtons(): ReactNode {
		let portalInfo: Information = this.props.context.info;
		let providerButtons: Array<ReactNode> = [];
		let portalUrl: string = portalConfiguration.portalUrl.toString();

		for (let oauthProvider of FunctionHelper.applyValue(portalInfo.oauth, Information.getOauthDefault())) {
			let config: any = {
				client_id: oauthProvider.clientId,
				redirect_uri: portalUrl + OAuth.route,
				authorization_endpoint: oauthProvider.oidc?.authorizationEndpoint,
				token_endpoint: oauthProvider.oidc?.tokenEndpoint
			};

			if (FunctionHelper.exists(oauthProvider.scope) && oauthProvider.scope !== "") {
				config.requested_scopes = oauthProvider.scope;
			}

			let pkce: PKCE = new PKCE(config);

			let additionalParams: any = {
				state: "webPDF-Portal"
			};

			if (FunctionHelper.exists(oauthProvider.applicationId) && oauthProvider.applicationId !== "") {
				additionalParams.audience = oauthProvider.applicationId;
			}

			providerButtons.push(
				<Button key={oauthProvider.name}
						onClick={async () => {
							sessionStorage.setItem(StorageItem.OAUTH_REDIRECT, portalUrl + Portal.route);
							localStorage.setItem(StorageItem.PROVIDER, oauthProvider.name!);
							window.location.href = pkce.authorizeUrl(additionalParams);
						}}>
					{this.renderIcon(oauthProvider)}
					<Translate {...this.props} data={"login.provider"} namespace="dialog"
							   values={{provider: oauthProvider.displayName || oauthProvider.name}}/>
				</Button>
			)
		}

		if (providerButtons.length === 0) {
			return null;
		}

		return (
			<div className="provider-buttons p-col-12 p-grid">
				{portalInfo.localAuth?.enabled &&
					<div className="p-col-12">
						<h2><span><Translate {...this.props} data="login.or" namespace="dialog"/></span></h2>
					</div>
				}
				<div className="p-col-12 p-grid">
					{providerButtons}
				</div>
			</div>
		);
	}

	protected renderError(error?: Exception): React.ReactNode {
		let exception: Exception | undefined = error || this.state.exception;

		if (!FunctionHelper.exists(exception)) {
			return null;
		}

		let message = exception!.message;
		switch (exception?.code) {
			case ErrorCode.WEBSERVICE_SESSION_TOKEN_INCORRECT_CLAIM:
				message = Translate.value("errors.login.expired", {ns: "message"});
				break;
			default:
				return super.renderError(error);
		}

		let codes: string = "";
		if (FunctionHelper.exists(exception!.code)) {
			codes += "(errorcode: " + exception!.code;

			if (FunctionHelper.exists(exception!.exitCode)) {
				codes += ", exitcode: " + exception!.exitCode;
			}

			codes += ")";
		}

		return (
			<div className="p-col-12">
				<MessageBox {...this.props} type={MessageType.DANGER} closeable={true}
							onClose={() => this.setState({
								exception: undefined
							})}
							stacktrace={
								<>
									<p>{exception!.message} {codes}</p>
									<pre>{exception!.stack}</pre>
								</>
							}>
					{message}
				</MessageBox>
			</div>
		);
	}

	public renderDialog(): ReactNode {
		let parameter: LoginType | undefined = this.state.parameter;
		let errors: Map<LoginDialogFields, string> = this.state.fieldErrors;
		let portalInfo: Information = this.props.context.info;

		let classList: Array<string> = ["login-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide} resizable={false}
					closeOnEscape={false} header={this.renderHeader()} footer={<div/>}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{!portalInfo.localAuth?.enabled && (FunctionHelper.applyValue(portalInfo.oauth, [])).length === 0 ?
						<MessageBox {...this.props} type={MessageType.WARNING} className="p-col-12">
							<Translate {...this.props} data="infos.portal.no_auth" namespace="message"/>
						</MessageBox> :
						<>
							{this.renderError()}
							<fieldset className="p-col-12 p-grid">
								{portalInfo.localAuth?.enabled &&
									<>
										<div className="p-col-12 p-grid"
											 hidden={!this.state.fields?.contains(LoginDialogFields.USERNAME)}>
											<label className="p-col-12 p-sm-4" htmlFor="control-username">
												<Translate {...this.props} data="login.username" namespace="dialog"/>
											</label>
											<div className="p-col-12 p-sm-8 p-grid">
												<InputText id="control-username" autoFocus={true}
														   className={
															   errors.has(LoginDialogFields.USERNAME) ? "p-invalid" : ""
														   }
														   tooltip={errors.has(LoginDialogFields.USERNAME) ?
															   Translate.value(
																   errors.get(LoginDialogFields.USERNAME)!,
																   {ns: "message"}
															   ) : ""}
														   value={FunctionHelper.applyValue(
															   parameter?.user, LoginType.getUserDefault()
														   )}
														   onInput={(e) => this.onChange(
															   LoginDialogFields.USERNAME, e.currentTarget.value
														   )}/>
											</div>
										</div>
										<div className="p-col-12 p-grid"
											 hidden={!this.state.fields?.contains(LoginDialogFields.PASSWORD)}>
											<label className="p-col-12 p-sm-4" htmlFor="control-password">
												<Translate {...this.props} data="login.password" namespace="dialog"/>
											</label>
											<div className="p-col-12 p-sm-8 p-grid">
												<Password id="control-password" feedback={false} toggleMask={true}
														  className={
															  errors.has(LoginDialogFields.PASSWORD) ? "p-invalid" : ""
														  }
														  tooltip={errors.has(LoginDialogFields.PASSWORD) ?
															  Translate.value(
																  errors.get(LoginDialogFields.PASSWORD)!,
																  {ns: "message"}
															  ) : ""}
														  value={FunctionHelper.applyValue(
															  parameter?.password, LoginType.getPasswordDefault()
														  )}
														  onInput={(e) => this.onChange(
															  LoginDialogFields.PASSWORD, e.currentTarget.value
														  )}/>
											</div>
										</div>
										<div className="p-col-12 p-grid">
											{this.renderFooter()}
										</div>
									</>
								}
								{this.renderButtons()}
							</fieldset>
						</>
					}
				</div>
			</Dialog>
		);
	}

	protected onChange(field: LoginDialogFields, value: any): void {
		let parameter: LoginType = this.state.parameter!;
		let errors: Map<LoginDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case LoginDialogFields.USERNAME:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.user = value;
				break;
			case LoginDialogFields.PASSWORD:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.password = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeaderIcon(): ReactNode {
		let portalInfo: Information = this.props.context.info;

		switch (portalInfo.localAuth?.userStorage) {
			case UserStorage.AzureAd:
				return <img src={azure} alt="Microsoft"/>;
			case UserStorage.Ldap:
				return <Icon icon={"icon-user ldap"}/>;
			case UserStorage.Database:
				return <Icon icon={"icon-user database"}/>;
		}

		return <Icon icon={"icon-user"}/>;
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					{this.renderHeaderIcon()}
					<Translate {...this.props} data="login.title" namespace="dialog"/>
				</h1>
			</div>
		);
	}

	/**
	 * Render the dialog footer
	 */
	protected renderFooter(): ReactNode {
		let portalInfo: Information = this.props.context.info;

		return (
			<div className="p-col-12 p-grid" style={{
				justifyContent: "flex-end"
			}}>
				<Button label={Translate.value("buttons.login")} className="primary-button"
						icon={"icon" + (this.state.submitted ? " icon-spinner spin" : " icon-login")}
						disabled={!this.isValid()} onClick={() => this.submit()}/>
				{portalInfo.localAuth?.allowAnonymous &&
					<Button label={Translate.value("buttons.anonymous")} disabled={this.state.submitted}
							icon={"icon" + (this.state.submitted ? " icon-spinner spin" : " icon-user")}
							className="dialog-button" onClick={() => this.loginAnonymous()}
							style={{
								marginLeft: "20px"
							}}/>
				}
			</div>
		);
	}

	protected isValid(): boolean {
		if (!this.mounted || !this.props.visible) {
			return false;
		}

		let parameter: LoginType = this.state.parameter!;

		if (!FunctionHelper.exists(parameter.user) || FunctionHelper.isEmpty(parameter.user!) ||
			!FunctionHelper.exists(parameter.password) || FunctionHelper.isEmpty(parameter.password!)) {
			return false;
		}

		let errors: Map<any, string> = this.state.fieldErrors;
		return errors.size === 0 && !this.state.submitted;
	}

	private async loginAnonymous(): Promise<void> {
		await super.submit();

		try {
			await this.props.context.session.login();
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.submit();
			}
			return;
		}

		this.props.context.getPage().addMessage(
			new Message("success.authentication.login", MessageType.SUCCESS)
		);
		this.hide();
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let operationData: LoginType = this.state.parameter!.clone();

		try {
			await this.props.context.session.login(undefined, operationData.user, operationData.password);
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.submit();
			}
			return;
		}

		this.props.context.getPage().addMessage(
			new Message("success.authentication.login", MessageType.SUCCESS)
		);
		this.hide();
	}

	protected async onShow(): Promise<void> {
		await super.onShow();

		let storageOAuthError: string | null = sessionStorage.getItem(StorageItem.OAUTH_ERROR);
		if (storageOAuthError !== null) {
			let oauthError: OAuthError = JSON.parse(storageOAuthError!) as OAuthError;

			if (oauthError.raw !== "") {
				let error: Exception = new Exception(oauthError.description);
				error.name = FunctionHelper.applyValue(oauthError.name, "");
				error.stack = FunctionHelper.applyValue(oauthError.raw, "");
				error.code = FunctionHelper.applyValue(oauthError.code, 0);

				this.setState({
					exception: error
				});
			}

			localStorage.removeItem(StorageItem.PROVIDER);
			sessionStorage.removeItem(StorageItem.OAUTH_ERROR);
		}
	}

	protected getDialogType(): DialogType {
		return DialogType.LOGIN;
	}

	protected getDefaultParameter(): LoginType {
		return (DialogDefault.getParameter(this.getDialogType()) as LoginType).clone();
	}

	protected async handleKeydown(event: KeyboardEvent): Promise<void> {
		if (!this.mounted || !this.props.visible) {
			return;
		}

		let parameter: LoginType = this.state.parameter!;
		let userSet: boolean = FunctionHelper.exists(parameter.user) && !FunctionHelper.isEmpty(parameter.user!);
		let passwordSet: boolean = FunctionHelper.exists(parameter.password) && !FunctionHelper.isEmpty(parameter.password!);

		if (event.key.toLowerCase() === "enter") {
			if (this.isValid() && !this.state.submitted) {
				await this.submit();
			} else if (!userSet && !passwordSet) {
				await this.loginAnonymous();
			}
		}
	}
}

const LoginDialog = React.memo(LoginDialogDefinition,
	(prevProps: Readonly<LoginDialogProperties>, nextProps: Readonly<LoginDialogProperties>) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default LoginDialog;